import React, { useEffect, useState } from "react";
import { Button, Form, Input, Spin, Typography, message, Switch, InputNumber, Divider, theme } from "antd";
import { useSelector } from "react-redux";
import { Post } from "../../helpers/apicalls/apicalls";

/**
 * Per-brand AI: toggles, auto-reply delay, bot name, extension prompts.
 * `brandToken` is the brand widget token (route :brandId).
 */
export default function BrandAIBot({ brandToken }) {
  const { token } = theme.useToken();
  const userData = useSelector((state) => state.Auth.userData);
  const [form] = Form.useForm();
  const isAiAutoReply = Form.useWatch("isAiAutoReply", form);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveHover, setSaveHover] = useState(false);

  useEffect(() => {
    if (!brandToken || !userData?.companyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await Post("/agents/user/get-brand-ai-bot", {
          companyId: userData.companyId,
          websiteToken: brandToken,
        });
        if (cancelled) return;
        if (res?.status && res?.data) {
          form.setFieldsValue({
            isAiActive: res.data.isAiActive ?? false,
            isAiAutoReply: res.data.isAiAutoReply ?? false,
            aiAutoReplyTimeoutSeconds: res.data.aiAutoReplyTimeoutSeconds ?? 30,
            name: res.data.name || "Assistant",
            extensionContent: res.data.extensionContent || "",
          });
        } else {
          form.setFieldsValue({
            isAiActive: false,
            isAiAutoReply: false,
            aiAutoReplyTimeoutSeconds: 30,
            name: "Assistant",
            extensionContent: "",
          });
        }
      } catch (e) {
        if (!cancelled) {
          message.error(e?.message || "Failed to load AI bot settings");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brandToken, userData?.companyId, form]);

  const onFinish = async () => {
    if (!brandToken || !userData?.companyId) {
      message.error("Missing brand or company.");
      return;
    }
    // Include fields for controls that are hidden (e.g. timer when auto-reply is off)
    const values = form.getFieldsValue(true);
    setSaving(true);
    try {
      const res = await Post("/agents/user/save-brand-ai-bot", {
        companyId: userData.companyId,
        websiteToken: brandToken,
        name: values.name,
        extensionContent: values.extensionContent ?? "",
        isAiActive: Boolean(values.isAiActive),
        isAiAutoReply: Boolean(values.isAiAutoReply),
        aiAutoReplyTimeoutSeconds:
          values.aiAutoReplyTimeoutSeconds != null && values.aiAutoReplyTimeoutSeconds !== ""
            ? Number(values.aiAutoReplyTimeoutSeconds)
            : 30,
      });
      if (res?.status) {
        message.success(res?.message || "Saved");
        if (res?.data) {
          form.setFieldsValue({
            isAiActive: res.data.isAiActive,
            isAiAutoReply: res.data.isAiAutoReply,
            aiAutoReplyTimeoutSeconds: res.data.aiAutoReplyTimeoutSeconds,
            name: res.data.name,
            extensionContent: res.data.extensionContent ?? "",
          });
        }
      } else {
        message.error(res?.message || "Save failed");
      }
    } catch (e) {
      message.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!brandToken) {
    return (
      <div style={{ padding: 16 }}>
        <Typography.Text type="secondary">Open AI Bot from a brand (Widget settings).</Typography.Text>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 280,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="brandAiBot" style={{ padding: "8px 0 0 0", width: "100%", minWidth: 0, maxWidth: 720 }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Control AI suggestions for agents, auto-reply timing for visitors, the bot name in chat, and prompts for this brand.
      </Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          isAiActive: false,
          isAiAutoReply: false,
          aiAutoReplyTimeoutSeconds: 30,
          name: "Assistant",
          extensionContent: "",
        }}
      >
        <Typography.Text strong>AI features</Typography.Text>
        <Form.Item
          name="isAiActive"
          label="AI Suggestion"
          valuePropName="checked"
          extra="Suggest replies in the agent chat (dashboard)."
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="isAiAutoReply"
          label="AI auto reply"
          valuePropName="checked"
          extra="When enabled, the bot can respond to visitors (subject to prompts and the bot record)."
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="aiAutoReplyTimeoutSeconds"
          label="Auto-reply delay (seconds)"
          style={{ display: isAiAutoReply ? "block" : "none" }}
          extra={isAiAutoReply ? "Wait time after the visitor sends a message before the AI can reply (when auto-reply is used)." : undefined}
          rules={[
            {
              validator: async (_, v) => {
                if (!isAiAutoReply) return;
                if (v == null || v === "") {
                  throw new Error("Enter delay in seconds");
                }
                const n = Number(v);
                if (!Number.isFinite(n) || n < 5 || n > 600) {
                  throw new Error("Use 5–600 seconds");
                }
              },
            },
          ]}
        >
          <InputNumber min={5} max={600} style={{ width: "100%" }} />
        </Form.Item>

        <Divider />

        <Typography.Text strong>Bot profile</Typography.Text>
        <Form.Item
          name="name"
          label="Bot display name"
          rules={[{ required: true, message: "Enter a name" }]}
        >
          <Input placeholder="e.g. Support Assistant" maxLength={120} />
        </Form.Item>
        <Form.Item
          name="extensionContent"
          label="Prompts"
        >
          <Input.TextArea
            rows={8}
            placeholder="Additional context or tone for this brand."
          />
        </Form.Item>

        <Form.Item className="brandAiBot__save">
          <Button
            disabled={saving}
            type="primary"
            onClick={() => form.submit()}
          >
            Save Changes
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
